/**
 * SuperAdminMultiTenantConsolidate — Iteration 93.
 *
 * Super-Admin-Tool zum Reparieren von Multi-Tenant-Usern, deren
 * `user_tenant_memberships`-Zeilen fehlen (deshalb kein Switcher). Zeigt
 * eine Diagnose aller doppelt vorhandenen E-Mails über Mandanten hinweg
 * und aller Direktoren/Manager ohne Membership-Zeilen.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import {
  Wrench, RefreshCw, Loader2, AlertTriangle, Users, ChevronRight,
  ShieldAlert, CheckCircle2,
} from "lucide-react";

export default function SuperAdminMultiTenantConsolidate() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState(null);
  const [previewFor, setPreviewFor] = useState(null); // {email, plan}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/superadmin/multi-tenant/diagnose");
      setDiag(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnose fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dryRun = async (email) => {
    setBusyEmail(email);
    try {
      const r = await api.post("/superadmin/multi-tenant/consolidate",
        { email, dry_run: true });
      setPreviewFor({ email, plan: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally {
      setBusyEmail(null);
    }
  };

  const commit = async (email) => {
    if (!window.confirm(
      `Wirklich alle Duplikat-User-Records für ${email} zusammenführen? ` +
      `Diese Aktion aktualisiert Angebote, Projekte und weitere Verknüpfungen. ` +
      `Sie ist idempotent, aber nicht trivial rückgängig zu machen.`
    )) return;
    setBusyEmail(email);
    try {
      const r = await api.post("/superadmin/multi-tenant/consolidate",
        { email, dry_run: false });
      toast.success("Konsolidierung abgeschlossen. Der Nutzer sieht jetzt den Mandanten-Switcher beim nächsten Login.");
      setPreviewFor({ email, plan: r.data });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konsolidierung fehlgeschlagen");
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <>
      <AppHeader />
      <div className="mx-auto max-w-5xl px-4 py-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/15 border border-amber-500/40">
              <Wrench size={22} className="text-amber-300" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-zinc-100">Multi-Tenant Reparatur</h1>
              <p className="text-[13px] text-zinc-300 font-medium">
                Duplikat-User-Records zusammenführen, damit der Mandanten-Switcher erscheint
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="mt-refresh"
            className="h-10 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Aktualisieren
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-zinc-400"><Loader2 className="animate-spin mx-auto mb-2" /> Diagnose läuft…</div>
        )}

        {!loading && diag && (
          <>
            <section className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="text-amber-400" size={18} />
                <h2 className="font-display text-[16px] font-bold text-zinc-100">Doppelte E-Mails über Mandanten hinweg</h2>
                <span className="ml-auto text-xs text-zinc-400 font-medium">{diag.duplicate_emails?.length || 0} betroffen</span>
              </div>
              {(diag.duplicate_emails || []).length === 0 ? (
                <div className="rounded-xl border border-zinc-800 bg-[#18181B] p-4 text-[13px] text-zinc-400 flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#00E575]" />
                  Keine doppelten E-Mails gefunden.
                </div>
              ) : (
                <div className="space-y-2">
                  {diag.duplicate_emails.map((d) => (
                    <div key={d.email} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3.5" data-testid={`mt-dup-${d.email}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-[13.5px] text-amber-200 font-bold">{d.email}</div>
                          <div className="text-[12px] text-zinc-300 mt-1">
                            <b className="text-zinc-100">{d.record_count}</b> User-Records
                            {d.names?.length > 0 && <> · {d.names[0]}</>}
                            {" "}· Mandanten: {d.tenant_names?.join(" · ")}
                          </div>
                          <div className="text-[11px] text-zinc-400 mt-0.5 font-mono">
                            Memberships bereits vorhanden: {d.existing_memberships}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            onClick={() => dryRun(d.email)}
                            disabled={busyEmail === d.email}
                            data-testid={`mt-preview-${d.email}`}
                            className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-[12.5px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                          >
                            {busyEmail === d.email ? <Loader2 className="animate-spin" size={14} /> : <ChevronRight size={14} />}
                            Vorschau
                          </button>
                          <button
                            onClick={() => commit(d.email)}
                            disabled={busyEmail === d.email}
                            data-testid={`mt-commit-${d.email}`}
                            className="h-9 px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/50 text-[12.5px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                          >
                            <Wrench size={14} /> Zusammenführen
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="text-red-400" size={18} />
                <h2 className="font-display text-[16px] font-bold text-zinc-100">Direktoren/Manager ohne Memberships</h2>
                <span className="ml-auto text-xs text-zinc-400 font-medium">{diag.elevated_users_without_memberships?.length || 0}</span>
              </div>
              {(diag.elevated_users_without_memberships || []).length === 0 ? (
                <div className="rounded-xl border border-zinc-800 bg-[#18181B] p-4 text-[13px] text-zinc-400 flex items-center gap-2">
                  <CheckCircle2 size={16} className="text-[#00E575]" />
                  Alle Direktoren/Manager haben Membership-Zeilen.
                </div>
              ) : (
                <div className="rounded-xl border border-zinc-800 bg-[#18181B] divide-y divide-zinc-800">
                  {diag.elevated_users_without_memberships.map((u) => (
                    <div key={u.id} className="p-3 flex items-center gap-2" data-testid={`mt-elev-${u.id}`}>
                      <Users size={14} className="text-zinc-400 flex-shrink-0" />
                      <div className="text-[12.5px] text-zinc-100 min-w-0 flex-1">
                        <b>{u.name}</b> · <span className="text-zinc-400">{u.email}</span>
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-zinc-800 text-[10px] font-bold text-zinc-300">{u.role}</span>
                        <span className="ml-2 text-zinc-400 font-mono text-[11px]">→ {u.tenant_name || u.tenant_id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[11.5px] text-zinc-400 mt-2 italic">
                Wenn diese Direktoren mehreren Mandanten angehören sollen, muss ihre E-Mail-Adresse
                in beiden Mandanten als User-Record existieren (dann als Duplikat in der Liste oben)
                oder direkt eine Membership über einen Admin-Flow angelegt werden.
              </div>
            </section>
          </>
        )}

        {previewFor && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setPreviewFor(null)}>
            <div
              className="bg-[#18181B] border border-zinc-700 rounded-2xl max-w-2xl w-full p-5 max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-display text-lg font-bold text-zinc-100 mb-1">
                Konsolidierungs-{previewFor.plan.dry_run ? "Vorschau" : "Ergebnis"}
              </h3>
              <div className="text-[12.5px] text-zinc-400 mb-3">{previewFor.email}</div>
              <div className="space-y-3 text-[12.5px] text-zinc-200">
                <Row label="Primary User-ID" value={<span className="font-mono">{previewFor.plan.primary_user_id}</span>} />
                <Row label="Duplikate" value={previewFor.plan.duplicates?.length} />
                <Row label="Neue Memberships" value={previewFor.plan.memberships_to_create?.length} />
                <Row label="FK-Updates" value={
                  <div className="space-y-0.5">
                    {Object.entries(previewFor.plan.fk_updates || {}).map(([k, v]) => (
                      <div key={k} className="font-mono text-[11px]">{k}: <b>{v}</b></div>
                    ))}
                    {Object.keys(previewFor.plan.fk_updates || {}).length === 0 && <span className="text-zinc-500">keine</span>}
                  </div>
                } />
                {!previewFor.plan.dry_run && (
                  <div className="text-[13px] text-[#00E575] font-bold flex items-center gap-1.5 mt-2">
                    <CheckCircle2 size={14} /> Erfolgreich konsolidiert.
                  </div>
                )}
              </div>
              <button
                onClick={() => setPreviewFor(null)}
                className="mt-4 h-10 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-sm w-full"
              >
                Schließen
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="text-zinc-400 font-medium">{label}</div>
      <div>{value ?? "—"}</div>
    </div>
  );
}
